import { LazyErrorBoundary, LazyLoadingFallback, LazyLottie } from '@/lib/lazy-components'
import MCPVerify from '@/styles/animations/mcp-verify.json'
import MCPLoader from '@/styles/animations/mcp-loader.json'
import MCPSuccess from '@/styles/animations/mcp-success.json'
import type { LottieComponentProps } from 'lottie-light-react'
import React from 'react'
import { usePrevious } from 'react-use'

export const MCPAnimation = (
  props: Omit<LottieComponentProps, 'animationData' | 'ref'> & {
    loading: boolean
    bypassVerify?: boolean
    error?: boolean
  },
) => {
  const { loading, bypassVerify = false, error = false } = props
  const prevLoading = usePrevious(loading)
  const [animationState, setAnimationState] = React.useState<'verify' | 'loading' | 'success'>(
    () => (bypassVerify ? 'loading' : 'verify'),
  )

  const handleVerifyComplete = () => {
    setAnimationState('loading')
  }

  React.useEffect(() => {
    if (animationState !== 'verify' && error) {
      setAnimationState('verify')
    } else if (animationState === 'loading' && prevLoading && !loading) {
      setAnimationState('success')
    }
  }, [loading, animationState, error])

  const getAnimation = React.useCallback(() => {
    switch (animationState) {
      case 'verify':
        return (
          <LazyLottie
            key={loading ? 'playing' : 'idle'}
            animationData={MCPVerify}
            loop={false}
            autoplay={loading}
            onComplete={handleVerifyComplete}
          />
        )
      case 'loading':
        return <LazyLottie animationData={MCPLoader} />
      case 'success':
        return <LazyLottie animationData={MCPSuccess} loop={false} />
      default:
        return null
    }
  }, [loading, animationState])

  return (
    <LazyErrorBoundary>
      <React.Suspense fallback={<LazyLoadingFallback className='h-8 w-8' />}>
        {getAnimation()}
      </React.Suspense>
    </LazyErrorBoundary>
  )
}
